import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { createBirthdayRequest, apiError } from "../lib/api";
import { TIME_SLOTS, slotLabel } from "../lib/events";
import { BrandWordmark } from "../components/Brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function BirthdayRequest() {
  const { t } = useLang();
  const [form, setForm] = useState({
    parent_first_name: "", parent_last_name: "", phone_number: "",
    child_name: "", child_age: "", requested_date: "", preferred_time: "",
    alt_time: "", est_children: "", est_adults: "", special_requests: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const required = ["parent_first_name", "parent_last_name", "phone_number",
      "child_name", "child_age", "requested_date", "preferred_time", "est_children", "est_adults"];
    for (const k of required) {
      if (!String(form[k]).trim()) return toast.error(t("fillRequiredFields"));
    }
    if (form.phone_number.replace(/\D/g, "").length < 7) return toast.error(t("pleaseEnterValidPhone"));
    if (form.requested_date < todayStr()) return toast.error(t("dateCannotBePast"));
    setLoading(true);
    try {
      await createBirthdayRequest(form);
      setDone(true);
    } catch (e) {
      toast.error(apiError(e) || t("somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFEEBC]" data-testid="birthday-request-page">
      <div className="lf-grain min-h-screen">
        <header className="mx-auto flex max-w-2xl items-center justify-between px-5 py-6">
          <BrandWordmark />
          <LanguageSwitcher />
        </header>

        <main className="mx-auto max-w-2xl px-5 pb-16">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              className="mt-8 rounded-3xl bg-white p-8 text-center shadow-xl"
              data-testid="birthday-success"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#26422A]">
                <CheckCircle2 className="h-8 w-8 text-[#FFEEBC]" />
              </div>
              <h1 className="font-display text-2xl font-black text-[#26422A]">{t("thankYou")}</h1>
              <p className="mt-3 text-sm leading-relaxed text-[#26422A]/90">{t("birthdaySuccessMsg")}</p>
              <Button
                data-testid="birthday-new-request-btn"
                onClick={() => { setDone(false); setForm({
                  parent_first_name: "", parent_last_name: "", phone_number: "",
                  child_name: "", child_age: "", requested_date: "", preferred_time: "",
                  alt_time: "", est_children: "", est_adults: "", special_requests: "" }); }}
                className="mt-6 h-12 rounded-xl bg-[#26422A] px-6 font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
              >
                {t("submitAnother")}
              </Button>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="font-display text-3xl font-black leading-tight text-[#26422A] sm:text-4xl">
                {t("birthdayTitle")}
              </h1>
              <p className="mb-8 mt-3 text-sm leading-relaxed text-[#26422A]/90">{t("birthdaySubtitle")}</p>

              <div className="space-y-6 rounded-3xl bg-white p-6 shadow-xl sm:p-8">
                {/* Parent info */}
                <Section title={t("parentInfo")}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t("firstName")} required>
                      <Input data-testid="bf-first-name" value={form.parent_first_name}
                        onChange={(e) => set("parent_first_name", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label={t("lastName")} required>
                      <Input data-testid="bf-last-name" value={form.parent_last_name}
                        onChange={(e) => set("parent_last_name", e.target.value)} className={inputCls} />
                    </Field>
                  </div>
                  <Field label={t("phone")} required>
                    <Input data-testid="bf-phone" type="tel" value={form.phone_number}
                      onChange={(e) => set("phone_number", e.target.value)} className={inputCls} />
                  </Field>
                </Section>

                {/* Child info */}
                <Section title={t("childInfo")}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t("childName")} required>
                      <Input data-testid="bf-child-name" value={form.child_name}
                        onChange={(e) => set("child_name", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label={t("childAge")} required>
                      <Input data-testid="bf-child-age" type="number" min="0" value={form.child_age}
                        onChange={(e) => set("child_age", e.target.value)} className={inputCls} />
                    </Field>
                  </div>
                </Section>

                {/* Party details */}
                <Section title={t("partyDetails")}>
                  <Field label={t("requestedDate")} required>
                    <Input data-testid="bf-date" type="date" min={todayStr()} value={form.requested_date}
                      onChange={(e) => set("requested_date", e.target.value)} className={inputCls} />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t("preferredTime")} required>
                      <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                        <SelectTrigger data-testid="bf-time" className={inputCls}><SelectValue placeholder={t("selectTime")} /></SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`bf-time-${s.value}`}>{slotLabel(s.value, t)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label={t("altTime")}>
                      <Select value={form.alt_time} onValueChange={(v) => set("alt_time", v)}>
                        <SelectTrigger data-testid="bf-alt-time" className={inputCls}><SelectValue placeholder={t("selectTime")} /></SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{slotLabel(s.value, t)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label={t("estChildren")} required>
                      <Input data-testid="bf-est-children" type="number" min="0" value={form.est_children}
                        onChange={(e) => set("est_children", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label={t("estAdults")} required>
                      <Input data-testid="bf-est-adults" type="number" min="0" value={form.est_adults}
                        onChange={(e) => set("est_adults", e.target.value)} className={inputCls} />
                    </Field>
                  </div>
                  <Field label={t("specialRequests")}>
                    <Textarea data-testid="bf-notes" value={form.special_requests} rows={3}
                      onChange={(e) => set("special_requests", e.target.value)}
                      className="rounded-xl border-[#26422A]/40 font-medium text-[#26422A]" />
                  </Field>
                </Section>

                <Button
                  data-testid="birthday-submit-btn"
                  onClick={submit}
                  disabled={loading}
                  className="h-14 w-full rounded-xl bg-[#26422A] text-base font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("sendBirthdayRequest")}
                </Button>
              </div>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}

const inputCls = "h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 font-semibold text-[#26422A]";

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <h2 className="font-display text-base font-bold text-[#26422A]">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold text-[#26422A]">
        {label}{required && <span className="text-[#26422A]/50"> *</span>}
      </Label>
      {children}
    </div>
  );
}

